import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";
import { Paperclip, Loader2, X, FileText } from "lucide-react";

const ACCEPT = "image/png,image/jpeg,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.txt";

export function AttachmentUpload({ value = [], onChange, testid = "attach" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  async function pick(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: must be under 10MB`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/attachment", fd);
        added.push(data);
      } catch (err) { toast.error(`${file.name}: ${formatApiError(err)}`); }
    }
    onChange([...value, ...added]);
    setBusy(false);
  }
  return (
    <div className="space-y-2">
      <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testid}-btn`} className="border-violet-200 text-violet-700">
        {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Paperclip size={14} className="mr-1" />} Add photos / documents
      </Button>
      {value.length > 0 && <AttachmentList items={value} onRemove={(i) => onChange(value.filter((_, j) => j !== i))} />}
    </div>
  );
}

async function openDoc(a) {
  try {
    const r = await api.get(a.url.replace(/^\/api/, ""), { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = a.name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch { toast.error("Could not open file"); }
}

export function AttachmentList({ items = [], onRemove }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((a, i) => (
        <div key={a.url} className="relative group">
          {a.content_type?.startsWith("image/")
            ? <button type="button" onClick={() => openDoc(a)}><AuthImage src={a.url} alt={a.name} className="h-20 w-20 rounded-xl object-cover border border-violet-100" /></button>
            : <button type="button" data-testid={`attachment-${i}`} onClick={() => openDoc(a)} className="h-20 w-36 rounded-xl border border-violet-100 bg-violet-50 px-2 flex flex-col items-center justify-center text-xs text-violet-800"><FileText size={20} /><span className="truncate w-full text-center mt-1">{a.name}</span></button>}
          {onRemove && <button type="button" onClick={() => onRemove(i)} className="absolute -top-1.5 -right-1.5 bg-white rounded-full shadow p-0.5 text-slate-500 hover:text-red-600"><X size={13} /></button>}
        </div>
      ))}
    </div>
  );
}
